/* =============================================================================
   CREATIVE DETONATOR - TRIGGERS LIBRARY
   Handles: Category Filtering, Hero Descriptions, Categories View, Deep Linking
   ============================================================================= */

// XSS prevention - import sanitization utilities
import { escapeHtml, escapeAttr, sanitizeColor } from '../../../js/core/sanitize.js';

let allTriggers = [];
let allCategories = [];
let currentView = 'all';      // 'all' | 'categories' | letter code (A-O)

// Categories that need dark (black) text on their light background colors
const DarkTextCategories = ['C', 'D', 'F', 'J', 'M', 'N', 'O'];

/* -----------------------------------------------------------------------------
   INITIALIZATION
   ----------------------------------------------------------------------------- */

document.addEventListener('DOMContentLoaded', async () => {
  // 1. Load data from DataStore
  await DataStore.loadCategories();
  await DataStore.loadTriggers();

  allTriggers = DataStore.triggers || [];
  allCategories = DataStore.categories || [];

  // 2. Initialize filter chip click handlers
  initFilters();

  // 3. Initialize Random Trigger button
  initRandomTrigger();

  // 4. Check URL hash for deep linking (e.g., #category-a or #categories)
  parseUrlHash();

  // 5. Initial render
  renderPage();

  // 6. Listen for hash changes (browser back/forward)
  window.addEventListener('hashchange', () => {
    parseUrlHash();
    renderPage();
  });
});

/* -----------------------------------------------------------------------------
   URL HASH HANDLING
   ----------------------------------------------------------------------------- */

function parseUrlHash() {
  const hash = window.location.hash.toLowerCase(); // e.g., "#category-a" or "#categories"

  if (!hash) {
    currentView = 'all';
    return;
  }

  if (hash === '#categories') {
    currentView = 'categories';
    return;
  }

  if (hash.startsWith('#category-')) {
    const code = hash.replace('#category-', '').toUpperCase();
    // Validate category exists
    if (allCategories.find(c => c.code === code)) {
      currentView = code;
      return;
    }
  }

  // Fallback: invalid hash
  currentView = 'all';
}

function updateUrlHash() {
  if (currentView === 'all') {
    // Clear hash without causing scroll
    history.replaceState(null, null, window.location.pathname);
  } else if (currentView === 'categories') {
    window.location.hash = 'categories';
  } else {
    window.location.hash = `category-${currentView.toLowerCase()}`;
  }
}

/* -----------------------------------------------------------------------------
   MAIN RENDER FUNCTION
   ----------------------------------------------------------------------------- */

function renderPage() {
  // Update filter button states
  updateFilterButtonStates();

  // Get containers
  const heroContainer = document.getElementById('category-hero');
  const categoriesContainer = document.getElementById('categories-grid');
  const triggersContainer = document.getElementById('triggers-grid');

  // Reset all containers
  if (heroContainer) {
    heroContainer.classList.remove('active');
    heroContainer.innerHTML = '';
  }
  if (categoriesContainer) {
    categoriesContainer.classList.remove('active');
    categoriesContainer.innerHTML = '';
  }
  if (triggersContainer) {
    triggersContainer.classList.remove('hidden');
  }

  // Render based on current view
  if (currentView === 'categories') {
    renderCategoriesView();
  } else if (currentView === 'all') {
    renderTriggersView(null);
  } else {
    // Specific category letter
    renderCategoryHero(currentView);
    renderTriggersView(currentView);
  }
}

function updateFilterButtonStates() {
  document.querySelectorAll('.filter-chip').forEach(chip => {
    const category = chip.dataset.category;
    const view = chip.dataset.view;

    // Check if this button matches current view
    let isActive = false;

    if (view === 'categories' && currentView === 'categories') {
      isActive = true;
    } else if ((view === 'all' || category === 'all') && currentView === 'all') {
      isActive = true;
    } else if (category && category === currentView) {
      isActive = true;
    }

    if (isActive) {
      chip.classList.add('active');
    } else {
      chip.classList.remove('active');
    }
  });
}

/* -----------------------------------------------------------------------------
   CATEGORIES VIEW (Shows all 15 categories as cards)
   ----------------------------------------------------------------------------- */

function renderCategoriesView() {
  const container = document.getElementById('categories-grid');
  const triggersContainer = document.getElementById('triggers-grid');

  if (!container) return;

  // Hide triggers grid
  if (triggersContainer) {
    triggersContainer.classList.add('hidden');
  }

  // Render category cards
  container.innerHTML = allCategories.map(cat => renderCategoryCard(cat)).join('');
  container.classList.add('active');

  // Add click handlers to category cards
  container.querySelectorAll('.category-card-library').forEach(card => {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      const code = card.dataset.category;
      currentView = code;
      updateUrlHash();
      renderPage();
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
  });
}

function renderCategoryCard(category) {
  const color = CategoryColors[category.code] || '#000000';
  const isDarkText = DarkTextCategories.includes(category.code);
  const textColor = isDarkText ? '#000000' : '#FFFFFF';
  const basePath = DataStore.getBasePath();
  const imagePath = `${basePath}assets/images/categories/${category.code}.png`;

  return `
    <div class="category-card-library"
         data-category="${escapeAttr(category.code)}"
         style="--cat-color: ${sanitizeColor(color)}; --text-color: ${sanitizeColor(textColor)}">

      <!-- Image -->
      <div class="category-card-image">
        <img src="${escapeAttr(imagePath)}" alt="${escapeAttr(category.name)}" loading="lazy">
      </div>

      <!-- Content -->
      <div class="category-card-body">
        <div class="category-card-header">
          <span class="category-card-badge">${escapeHtml(category.code)}</span>
          <h3 class="category-card-name">${escapeHtml(category.name)}</h3>
        </div>
        <p class="category-card-mechanism">${escapeHtml(category.mechanism)}</p>
        <span class="category-card-cta">${escapeHtml(category.triggerCount)} TRIGGERS &rarr;</span>
      </div>
    </div>
  `;
}

/* -----------------------------------------------------------------------------
   CATEGORY HERO (Description box when letter is selected)
   ----------------------------------------------------------------------------- */

function renderCategoryHero(categoryCode) {
  const heroContainer = document.getElementById('category-hero');
  if (!heroContainer) return;

  const category = DataStore.getCategoryByCode(categoryCode);
  if (!category) return;

  const color = CategoryColors[categoryCode] || '#000000';
  const isDarkText = DarkTextCategories.includes(categoryCode);
  const textColor = isDarkText ? '#000000' : '#FFFFFF';

  // Set CSS variables
  heroContainer.style.setProperty('--cat-color', color);
  heroContainer.style.setProperty('--text-color', textColor);

  // Toggle text color class
  if (isDarkText) {
    heroContainer.classList.remove('light-text');
  } else {
    heroContainer.classList.add('light-text');
  }

  // Inject content
  heroContainer.innerHTML = `
    <div class="category-hero-badge">${escapeHtml(category.code)}</div>
    <div class="category-hero-content">
      <h2 class="category-hero-title">${escapeHtml(category.name)}</h2>
      <div class="category-hero-mechanism">${escapeHtml(category.mechanism)}</div>
      <p class="category-hero-description">${escapeHtml(category.description || '')}</p>
      <div class="category-hero-meta">${escapeHtml(category.triggerCount)} triggers in this category</div>
    </div>
  `;

  // Show container
  heroContainer.classList.add('active');
}

/* -----------------------------------------------------------------------------
   TRIGGERS VIEW (Grid of trigger cards)
   ----------------------------------------------------------------------------- */

function renderTriggersView(categoryFilter) {
  const container = document.getElementById('triggers-grid');
  const categoriesContainer = document.getElementById('categories-grid');

  if (!container) return;

  // Hide categories grid
  if (categoriesContainer) {
    categoriesContainer.classList.remove('active');
  }

  // Filter triggers
  let triggersToShow = allTriggers;
  if (categoryFilter) {
    triggersToShow = triggersToShow.filter(t => t.category === categoryFilter);
  }

  // Empty state
  if (triggersToShow.length === 0) {
    container.innerHTML = `
      <div class="triggers-empty">
        <p>No triggers found${categoryFilter ? ` for category ${escapeHtml(categoryFilter)}` : ''}</p>
      </div>
    `;
    // CSS handles display: grid via .triggers-grid-v2 class
    return;
  }

  // Render trigger cards
  container.innerHTML = triggersToShow.map(trigger => renderTriggerCardV2(trigger)).join('');
  // CSS handles display: grid via .triggers-grid-v2 class
}

function renderTriggerCardV2(trigger) {
  const color = CategoryColors[trigger.category] || '#000000';
  const basePath = DataStore.getBasePath();
  const imagePath = getTriggerImagePathFromCode(trigger.code, basePath);
  const catInfo = DataStore.getCategoryByCode(trigger.category) || { name: '', mechanism: '' };
  const isDarkText = DarkTextCategories.includes(trigger.category);
  const badgeText = isDarkText ? '#000000' : '#FFFFFF';

  return `
    <a href="detail.html?code=${escapeAttr(trigger.code)}"
       class="trigger-card-v2"
       style="--cat-color: ${sanitizeColor(color)}; --badge-text: ${sanitizeColor(badgeText)}"
       data-code="${escapeAttr(trigger.code)}"
       data-category="${escapeAttr(trigger.category)}">

      <!-- Image -->
      <div class="trigger-card-v2-image">
        <img src="${escapeAttr(imagePath)}" alt="${escapeAttr(trigger.name)}" loading="lazy"
             onerror="this.parentElement.innerHTML='<span class=\\'trigger-icon-fallback\\'>${escapeHtml(getTriggerIcon(trigger.code))}</span>'">
      </div>

      <!-- Content -->
      <div class="trigger-card-v2-content">
        <!-- Header: Badge + Name -->
        <div class="trigger-card-v2-header">
          <div class="trigger-card-v2-badge-wrap">
            <span class="trigger-card-v2-badge">${escapeHtml(trigger.code)}</span>
            <div class="trigger-card-v2-tooltip" style="--cat-color: ${sanitizeColor(color)}">
              <div class="trigger-card-v2-tooltip-title">${escapeHtml(catInfo.name).toUpperCase()}</div>
              <p class="trigger-card-v2-tooltip-desc">${escapeHtml(catInfo.mechanism)}</p>
            </div>
          </div>
          <h3 class="trigger-card-v2-name">${escapeHtml(trigger.name)}</h3>
        </div>

        <!-- Question (THE FOCUS) -->
        <p class="trigger-card-v2-question">${escapeHtml(trigger.question)}</p>
      </div>
    </a>
  `;
}

/* -----------------------------------------------------------------------------
   FILTER INITIALIZATION
   ----------------------------------------------------------------------------- */

function initFilters() {
  document.querySelectorAll('.filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const category = chip.dataset.category;
      const view = chip.dataset.view;

      // Determine new view
      if (view === 'categories' || category === 'categories') {
        currentView = 'categories';
      } else if (view === 'all' || category === 'all') {
        currentView = 'all';
      } else if (category) {
        currentView = category;
      }

      // Update URL and render
      updateUrlHash();
      renderPage();

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'instant' });
    });
  });
}

/* -----------------------------------------------------------------------------
   RANDOM TRIGGER BUTTON
   ----------------------------------------------------------------------------- */

function initRandomTrigger() {
  const btn = document.getElementById('random-trigger');
  if (btn) {
    btn.addEventListener('click', () => {
      if (allTriggers.length > 0) {
        const randomIndex = Math.floor(Math.random() * allTriggers.length);
        const randomTrigger = allTriggers[randomIndex];
        window.location.href = `detail.html?code=${randomTrigger.code}`;
      }
    });
  }
}
